import { BookOpen, Shapes, Palette, Music, Sun, Drama, HeartHandshake, Sparkles, Languages } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import DailyRhythm from "@/components/DailyRhythm";
import CaydenGallery from "@/components/CaydenGallery";
import { Reveal } from "@/components/Reveal";
import { FlowerDoodle, WavyUnderline, Bee, Ladybug } from "@/components/Doodles";

const EXPLORE = [
    { icon: BookOpen, label: "Stories & early literacy", color: "bg-sage/20" },
    { icon: Shapes, label: "Numbers & shapes", color: "bg-sun/25" },
    { icon: Palette, label: "Art & sensory play", color: "bg-coral/15" },
    { icon: Music, label: "Music & movement", color: "bg-skysoft" },
    { icon: Sun, label: "Nature & outdoor play", color: "bg-sun/25" },
    { icon: Drama, label: "Pretend play", color: "bg-skysoft" },
    { icon: HeartHandshake, label: "Kindness & feelings", color: "bg-coral/15" },
    { icon: Sparkles, label: "Independence skills", color: "bg-sage/20" },
];

export default function Program() {
    usePageTitle("Daily Rhythm & Program | Little Seeds Flourish Daycare");

    return (
        <div>
            <PageHero
                eyebrow="Our program"
                title={<>Learning Through Play, Every Single Day.</>}
                sub="Play isn't a break from learning — it IS learning. Here's what a day in the garden looks like."
                doodle="sun"
            />

            {/* Explore grid */}
            <section className="pb-16 md:pb-20" aria-labelledby="explore-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <h2 id="explore-heading" className="font-display font-semibold text-2xl md:text-3xl mb-8">
                            What your child will explore
                            <WavyUnderline className="block w-40 h-2.5 mt-2" color="#8FB38A" />
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                        {EXPLORE.map((e, i) => (
                            <Reveal key={e.label} delay={i * 0.05} className="h-full">
                                <div className="hover-lift bg-white border-2 border-soil/10 rounded-3xl p-5 md:p-6 h-full text-center" data-testid={`program-explore-item-${i + 1}`}>
                                    <span className={`inline-flex p-3.5 rounded-2xl ${e.color} mb-3`}>
                                        <e.icon size={26} className="text-soil" />
                                    </span>
                                    <p className="font-bold text-[15px] leading-snug">{e.label}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal>
                        <div
                            className="mt-14 md:mt-20 relative max-w-3xl mx-auto text-center bg-skysoft/40 border-2 border-sky/30 rounded-[2rem] p-8 md:p-10 overflow-hidden"
                            data-testid="bilingual-section"
                        >
                            <Bee className="absolute top-4 right-5 hidden sm:block" />
                            <p className="inline-flex items-center gap-2 bg-white/80 border-2 border-soil/10 rounded-full px-4 py-1.5 text-sm font-extrabold text-sageink mb-5">
                                <Languages size={15} /> Bilingual learning
                            </p>
                            <h2 className="font-display font-semibold text-2xl md:text-3xl mb-2">
                                Growing Up Bilingual
                                <WavyUnderline className="block w-36 h-2.5 mt-2 mx-auto" color="#A8D5E2" />
                            </h2>
                            <p className="text-soilmuted text-lg leading-relaxed mt-5">
                                At our daycare, we proudly offer bilingual instruction in English and Spanish, helping
                                children develop language skills from an early age. Through fun, interactive activities,
                                songs, stories, and play-based learning, we create a nurturing environment where children
                                can explore both languages, build confidence, and embrace cultural diversity.
                            </p>
                            <div className="flex flex-wrap justify-center gap-3 mt-6">
                                <span className="inline-flex items-center gap-2 bg-white border-2 border-soil/10 rounded-full px-4 py-2 font-display font-semibold text-soil shadow-sm" data-testid="bilingual-chip-english">
                                    Hello!
                                </span>
                                <span className="inline-flex items-center gap-2 bg-white border-2 border-soil/10 rounded-full px-4 py-2 font-display font-semibold text-soil shadow-sm" data-testid="bilingual-chip-spanish">
                                    ¡Hola!
                                </span>
                            </div>
                        </div>
                    </Reveal>

                    <CaydenGallery />

                    <Reveal delay={0.1}>
                        <div className="mt-10 bg-sage/10 border-2 border-sage/25 rounded-[2rem] p-7 md:p-9" data-testid="program-flourish-card">
                            <h3 className="font-display font-semibold text-xl md:text-2xl mb-3">How We Help Children Flourish</h3>
                            <p className="text-lg text-soilmuted leading-relaxed">
                                We nurture your child — socially, emotionally, physically, creatively, and intellectually.
                                Through play, exploration, meaningful relationships, and age-appropriate learning experiences,
                                children build confidence, curiosity, independence, kindness, and the skills they need to thrive.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Daily rhythm */}
            <div className="bg-creamsoft relative">
                <div className="absolute inset-0" aria-hidden="true">
                    <img
                        src="/images/crayon-garden.jpg"
                        alt=""
                        className="absolute right-0 top-0 w-52 opacity-[0.07]"
                    />
                    <Bee className="absolute right-8 top-10 hidden lg:block" />
                </div>
                <section className="py-16 md:py-24 relative" aria-labelledby="rhythm-heading">
                    <div className="max-w-5xl mx-auto px-5 sm:px-8">
                        <Reveal className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
                            <p className="inline-flex items-center gap-2 bg-white/80 border-2 border-soil/10 rounded-full px-4 py-1.5 text-sm font-extrabold text-sageink mb-4">
                                <FlowerDoodle size={15} /> Sample daily rhythm
                            </p>
                            <h2 id="rhythm-heading" className="font-display font-semibold text-3xl md:text-4xl leading-tight text-balance">
                                A Day at Little Seeds Flourish
                            </h2>
                            <p className="mt-4 font-bold text-soil">Growing, learning, and playing throughout the day.</p>
                            <p className="mt-3 text-soilmuted">
                                Our days follow a consistent yet flexible rhythm designed around children's developmental needs.
                                Times may vary slightly based on age, individual needs, activities, and the natural flow of the day.
                            </p>
                        </Reveal>
                        <DailyRhythm />
                    </div>
                </section>
            </div>

            {/* Care details */}
            <section className="py-16 md:py-24" aria-labelledby="meals-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-5">
                    {[
                        {
                            id: "meals",
                            title: "Meals included",
                            icon: <FlowerDoodle size={22} petals="#F6C544" />,
                            text: "Breakfast, lunch, and afternoon snack are included and follow USDA CACFP guidelines. Menus are posted weekly. Water is always available.",
                        },
                        {
                            id: "infants",
                            title: "Infants (Seedlings)",
                            icon: <FlowerDoodle size={22} petals="#8FB38A" />,
                            text: "Our littlest seeds receive responsive, individualized care based on their unique routines and needs. Infants are fed on demand, held and nurtured during feedings, and provided with safe, comfortable opportunities for rest, play, movement, and connection. Breastfeeding families are welcomed and supported.",
                        },
                        {
                            id: "toilet",
                            title: "Toilet Learning",
                            icon: <FlowerDoodle size={22} petals="#E07A5F" />,
                            text: "Toilet learning is approached with patience, encouragement, and respect for each child's readiness. We partner with families to provide consistency between home and daycare — without pressure or rushing the process. Every little milestone is worth celebrating!",
                        },
                    ].map((c, i) => (
                        <Reveal key={c.id} delay={i * 0.08} className="h-full">
                            <div className="hover-lift bg-creamsoft border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid={`program-care-card-${c.id}`}>
                                <span className="inline-flex p-2.5 rounded-xl bg-white border-2 border-soil/10 mb-4">{c.icon}</span>
                                <h3 className="font-display font-semibold text-xl mb-2">{c.title}</h3>
                                <p className="text-soilmuted text-[15px] leading-relaxed">{c.text}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
                <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-6">
                    <Reveal>
                        <div className="bg-skysoft border-2 border-sky rounded-[2rem] p-6 md:p-7 flex gap-4 items-start" data-testid="program-communication-card">
                            <Ladybug className="shrink-0 mt-1" />
                            <p className="text-lg text-soil leading-relaxed">
                                <span className="font-display font-semibold">Staying connected:</span> expect daily texts and
                                photos (with your photo permission) from Asia, so you always know how the day went.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTABand title="Want to see the rhythm in person?" />
        </div>
    );
}
